import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getNotifications, markNotificationsRead, clearNotifications } from '../../services/notificationsService';
import { downloadReportPdf } from '../../utils/reportPdf';
import toast from 'react-hot-toast';

/* Колокольчик. Уведомления приходят с сервера: копятся, пока их не очистят, а
   «прочитано» и «очищено» одинаковы на всех устройствах человека. Список
   обновляется сам — раз в 20 секунд и сразу при возвращении на вкладку. */

const POLL_MS = 20000;

const ago = (d) => {
  if (!d) return '';
  const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.floor(h / 24);
  return days === 1 ? 'вчера' : `${days} дн назад`;
};

const Ic = {
  webinar_live: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  webinar_soon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>,
  review: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  student_registered: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>,
  digest: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  homework_checked: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
};
Ic.summary = Ic.digest;

export default function NotificationBell({ variant }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const res = await getNotifications(50);
      setItems(res.data || []);
      setUnread(res.unread || 0);
    } catch { /* сеть моргнула — попробуем на следующем круге */ }
  }, [user]);

  // Живое обновление: раз в 20 секунд и сразу, когда вернулись на вкладку
  useEffect(() => {
    if (!user) return undefined;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, [user, load]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const readAll = async () => {
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    try { await markNotificationsRead(); } finally { load(); }
  };
  const clearAll = async () => {
    setItems([]); setUnread(0);
    try { await clearNotifications(); } finally { load(); }
  };
  // Удалить одно уведомление: сервер умеет убирать по списку id
  const removeOne = async (n) => {
    setItems(prev => prev.filter(x => x.id !== n.id));
    if (!n.read) setUnread(u => Math.max(0, u - 1));
    try { await clearNotifications([n.id]); } finally { load(); }
  };
  const openItem = async (n) => {
    if (!n.read) {
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread(u => Math.max(0, u - 1));
      markNotificationsRead([n.id]).catch(() => {});
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };
  const download = async (n) => {
    setBusy(true);
    try {
      await downloadReportPdf(n);
      if (!n.read) { markNotificationsRead([n.id]).catch(() => {}); load(); }
    } catch {
      toast.error('Не удалось собрать файл');
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <button className={`notif-bell${variant === 'inline' ? ' notif-bell--inline' : ''}`} onClick={() => setOpen(o => !o)} aria-label="Уведомления">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-head">
            <span>Уведомления</span>
          </div>
          <div className="notif-body">
            {items.length === 0 ? (
              <div className="notif-empty">
                <div className="notif-empty-ic">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                </div>
                <div className="notif-empty-t">Новых уведомлений нет</div>
                <div className="notif-empty-s">
                  {user.role === 'admin'
                    ? 'Здесь появятся новые ученики и сводка за неделю'
                    : 'Здесь появятся напоминания о вебинарах и итоги недели'}
                </div>
              </div>
            ) : items.map(n => {
              const isReport = n.type === 'digest' || n.type === 'summary';
              return (
                <div key={n.id} className={`notif-item t-${n.type}${n.read ? '' : ' unread'}`}>
                  <span className="notif-ic">{Ic[n.type] || Ic.digest}</span>
                  <button className="notif-del" title="Убрать уведомление" aria-label="Убрать уведомление" onClick={() => removeOne(n)}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <div className="notif-main">
                    <div className="notif-title">{n.title}</div>
                    <div className="notif-sub">{n.text}{n.text ? ' · ' : ''}{ago(n.createdAt)}</div>
                    {isReport
                      ? <button className="notif-act" disabled={busy} onClick={() => download(n)}>{busy ? 'Собираем…' : 'Скачать PDF'}</button>
                      : n.link && <button className="notif-act" onClick={() => openItem(n)}>Открыть →</button>}
                  </div>
                </div>
              );
            })}
          </div>
          {items.length > 0 && (
            <div className="notif-foot">
              <button className="notif-foot-btn" onClick={readAll} disabled={unread === 0}>Прочитать всё</button>
              <button className="notif-foot-btn danger" onClick={clearAll}>Очистить всё</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
